/**
 * What this number has done with us before.
 *
 * The repeat caller is the common case on an emergency line, and the one the old
 * system handled worst — three identity silos that never joined, so someone who
 * rang an hour ago was a stranger again.
 *
 * No patient name and no clinical detail. It shows that a known patient is
 * calling and links to the record; opening it is a separate action that carries
 * a reason and is access-logged.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, type CallerProfile } from '../api/client.js';

function when(iso: string): string {
  const then = new Date(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  const days = Math.round(mins / 1440);
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: '2-digit' });
}

function StageBadge({ c }: { c: CallerProfile['cases'][number] }) {
  const open = c.status === 'open';
  return (
    <span
      className={
        'rounded px-1.5 py-0.5 text-[10px] font-medium ' +
        (open ? 'bg-emerald-600/20 text-emerald-300' : 'bg-slate-700 text-slate-300')
      }
    >
      {open ? (c.ops_stage_key?.replace(/_/g, ' ') ?? 'open') : c.status}
    </span>
  );
}

export function CallerHistory({ phone, onNavigate }: { phone: string; onNavigate?: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['caller', phone],
    queryFn: () => api.get<CallerProfile>(`/callers/${encodeURIComponent(phone)}`),
    enabled: Boolean(phone) && phone !== 'withheld',
  });

  if (phone === 'withheld') {
    return <p className="text-xs text-slate-500">Number withheld — no history to look up.</p>;
  }
  if (isLoading) return <p className="text-xs text-slate-500">Looking up history…</p>;
  if (!data) return null;

  const { summary, cases, calls } = data;

  if (summary.totalCases === 0 && summary.totalCalls <= 1) {
    return <p className="text-xs text-slate-500">First time this number has contacted us.</p>;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-400">
        {summary.totalCalls} call{summary.totalCalls === 1 ? '' : 's'} · {summary.totalCases} case
        {summary.totalCases === 1 ? '' : 's'}
        {summary.firstSeen && <> · first contact {when(summary.firstSeen)}</>}
      </p>

      {cases.length > 0 && (
        <div>
          <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Cases
          </h4>
          <ul className="space-y-1">
            {cases.slice(0, 6).map((c) => (
              <li key={c.id}>
                <Link
                  to={`/cases/${c.id}`}
                  onClick={onNavigate}
                  className="flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-slate-800"
                >
                  <span className="font-mono text-xs text-slate-200">{c.case_number}</span>
                  <StageBadge c={c} />
                  {c.transported === true && (
                    <span className="text-[10px] text-slate-400">transported</span>
                  )}
                  {c.transported === false && (
                    <span className="text-[10px] text-slate-400">no transport</span>
                  )}
                  <span className="ml-auto shrink-0 text-[11px] text-slate-500">
                    {when(c.requested_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {calls.length > 0 && (
        <div>
          <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Calls
          </h4>
          <ul className="space-y-0.5">
            {calls.slice(0, 5).map((c) => (
              <li key={c.id} className="flex items-center gap-2 px-1.5 text-xs text-slate-400">
                <span>{c.direction === 'inbound' ? '↓' : '↑'}</span>
                <span>{c.status}</span>
                {c.case_number && <span className="font-mono text-slate-300">{c.case_number}</span>}
                {c.has_recording && <span className="text-[10px] text-sky-400">recorded</span>}
                <span className="ml-auto shrink-0 text-[11px] text-slate-500">
                  {when(c.started_at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
